import * as THREE from "three";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { FlyCamera } from "./flyCamera";
import { PlanCamera } from "./planCamera";
import { buildLot } from "./plane";
import { buildProperty } from "./property";
import { buildCompass } from "./compass";
import { PEAR_TREE } from "./config";
import { buildPearTree } from "./pearTree";
import { CanvasGestures, InputState, isCoarsePointer, isCompactHud } from "./input";
import { TouchHud } from "./touchHud";

export class App {
  private renderer: THREE.WebGLRenderer;
  private labels: CSS2DRenderer;
  private scene = new THREE.Scene();
  private fly: FlyCamera;
  private plan: PlanCamera;
  private mode: "fly" | "plan" = "plan";
  private fog: THREE.Fog;
  private clock = new THREE.Clock();
  private readout: { x: HTMLElement; y: HTMLElement; z: HTMLElement };
  private input = new InputState();
  private hud: TouchHud;

  constructor(canvas: HTMLCanvasElement) {
    const coarse = isCoarsePointer();
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, coarse ? 1.5 : 2));
    const { w, h } = this.viewport();
    this.renderer.setSize(w, h);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.labels = new CSS2DRenderer();
    this.labels.setSize(w, h);
    this.labels.domElement.id = "labels";
    this.labels.domElement.style.position = "absolute";
    this.labels.domElement.style.inset = "0";
    this.labels.domElement.style.pointerEvents = "none";
    this.labels.domElement.style.touchAction = "none";
    document.body.appendChild(this.labels.domElement);

    const camera = new THREE.PerspectiveCamera(52, w / h, 0.15, 4000);
    this.fly = new FlyCamera(canvas, camera, this.input);
    this.fly.camera.position.set(PEAR_TREE.x + 14, 8, PEAR_TREE.z - 8);
    this.fly.lookAt(new THREE.Vector3(PEAR_TREE.x, 5, PEAR_TREE.z));
    this.fly.captureHome();
    this.plan = new PlanCamera(canvas);

    this.scene.background = new THREE.Color(0x8ea3b5);
    this.fog = new THREE.Fog(0x8ea3b5, 60, 420);
    this.scene.fog = this.fog;
    this.renderer.setClearColor(0x8ea3b5, 1);

    this.setupLights(coarse);
    this.scene.add(buildLot());
    this.scene.add(buildProperty());
    this.scene.add(buildCompass());
    this.scene.add(buildPearTree());

    this.readout = {
      x: mustEl("pos-x"),
      y: mustEl("pos-y"),
      z: mustEl("pos-z"),
    };

    this.hud = new TouchHud(this.input, {
      setPlan: (on) => this.setPlan(on),
      zoomIn: () => this.zoom(1),
      zoomOut: () => this.zoom(-1),
      frame: () => this.frameView(),
    });

    new CanvasGestures(canvas, (g) => {
      if (g.type === "end") {
        this.fly.releasePivot();
        return;
      }
      if (g.type === "drag") {
        this.hud.dismissHint();
        if (this.mode === "plan") this.plan.panBy(g.dx, g.dy);
        else if (g.pointers === 1) this.fly.lookBy(g.dx, g.dy);
        else this.fly.orbitBy(g.dx, g.dy);
        return;
      }
      if (g.type === "rotate") {
        if (this.mode === "fly") this.fly.rotateBy(g.angle);
        return;
      }
      if (g.type === "pinch") {
        if (this.mode === "plan") this.plan.zoomAt(g.scale, g.x, g.y);
        else this.fly.pinchDolly(g.scale);
        return;
      }
      if (g.type === "dbltap" && this.mode === "plan") this.frameView();
    });

    this.setPlan(true);

    window.addEventListener("resize", () => this.resize());
    window.visualViewport?.addEventListener("resize", () => this.resize());
    window.visualViewport?.addEventListener("scroll", () => this.resize());
  }

  start(): void {
    this.clock.start();
    this.renderer.setAnimationLoop(() => this.tick());
  }

  private setupLights(coarse: boolean): void {
    const hemi = new THREE.HemisphereLight(0xd7e4ee, 0x2a2f36, 0.9);
    this.scene.add(hemi);

    const sun = new THREE.DirectionalLight(0xfff3d6, 1.55);
    sun.position.set(50, 80, 40);
    sun.castShadow = true;
    const map = coarse ? 1024 : 2048;
    sun.shadow.mapSize.set(map, map);
    sun.shadow.camera.near = 4;
    sun.shadow.camera.far = 220;
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 100;
    sun.shadow.camera.bottom = -100;
    sun.shadow.bias = -0.0003;
    this.scene.add(sun);
    this.scene.add(sun.target);

    const fill = new THREE.DirectionalLight(0x9bb8d4, 0.32);
    fill.position.set(-40, 40, -80);
    this.scene.add(fill);
  }

  private setPlan(on: boolean): void {
    this.mode = on ? "plan" : "fly";
    this.fly.enabled = !on;
    this.plan.enabled = on;
    this.hud.setMode(on);
    if (on) {
      this.plan.frame(this.aspect(), isCompactHud() ? 18 : 0);
      this.scene.fog = null;
      document.body.classList.add("is-plan");
      document.body.classList.remove("is-fly");
    } else {
      this.scene.fog = this.fog;
      document.body.classList.remove("is-plan");
      document.body.classList.add("is-fly");
    }
    for (const el of document.querySelectorAll(".ops-fly")) {
      el.toggleAttribute("hidden", on);
    }
    for (const el of document.querySelectorAll(".ops-plan")) {
      el.toggleAttribute("hidden", !on);
    }
  }

  private zoom(dir: 1 | -1): void {
    if (this.mode === "plan") this.plan.zoomBy(dir > 0 ? 1.12 : 0.9);
    else this.fly.dollyBy(dir > 0 ? 1.22 : 0.8);
  }

  private frameView(): void {
    if (this.mode === "plan") this.plan.frame(this.aspect(), isCompactHud() ? 18 : 0);
    else this.fly.resetHome();
  }

  private activeCamera(): THREE.Camera {
    return this.mode === "plan" ? this.plan.camera : this.fly.camera;
  }

  private tick(): void {
    const dt = Math.min(this.clock.getDelta(), 0.05);
    this.fly.update(dt);
    this.plan.update(dt);
    const cam = this.activeCamera();
    const p = cam.position;
    this.readout.x.textContent = p.x.toFixed(1);
    this.readout.y.textContent = p.y.toFixed(1);
    this.readout.z.textContent = p.z.toFixed(1);
    if (this.mode === "fly") {
      const d = p.length();
      this.fog.near = Math.max(40, d * 0.7);
      this.fog.far = Math.max(280, d * 4.5);
    }
    this.renderer.render(this.scene, cam);
    this.labels.render(this.scene, cam);
  }

  private resize(): void {
    const { w, h } = this.viewport();
    this.fly.camera.aspect = w / h;
    this.fly.camera.updateProjectionMatrix();
    this.plan.setAspect(w / h);
    this.renderer.setSize(w, h);
    this.labels.setSize(w, h);
  }

  private viewport(): { w: number; h: number } {
    return {
      w: Math.max(1, window.innerWidth),
      h: Math.max(1, window.innerHeight),
    };
  }

  private aspect(): number {
    const { w, h } = this.viewport();
    return w / h;
  }
}

function mustEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el;
}
